import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { assignSubscriptionToUser, classifySubscriptionCheckResult, getSubscription, postCheckDestination } from '../utils/subscriptionApi';
import { logout } from '../utils/authApi';
import { LoaderPinwheel, AlertTriangle, CheckCircle, XCircle } from 'lucide-react';
import Header from '../components/Header';
import Loading from '../components/elements/Loading';
import Button from '../components/elements/Button';
import ConfirmModal from '../components/ConfirmModal';

export default function SubscriptionCheckPage() {
    const [status, setStatus] = useState(null);
    const [paymentLink, setPaymentLink] = useState(null);
    const [statusMessage, setStatusMessage] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const isLoggedIn = sessionStorage.getItem('isLoggedIn') === 'true';
    const [refreshCount, setRefreshCount] = useState(0);
    const [searchParams] = useSearchParams();
    const [subscriptionKey] = useState(searchParams.get('key') || '');
    const location = useLocation();
    const [showSuccessModal, setShowSuccessModal] = useState(false);
    const navigate = useNavigate();

    useEffect(() => {
        document.title = "Subscription | Eternal Locker™";
    }, []);

    useEffect(() => {
        if (!isLoggedIn) {
            navigate('/');
            return;
        }

        setLoading(true);
        setError(null);
        getSubscription()
            .then(res => {
                const classification = classifySubscriptionCheckResult(res, subscriptionKey);
                switch (classification.type) {
                    case 'assign':
                        try {
                            assignSubscriptionToUser(subscriptionKey)
                                .then(assignRes => {
                                    if (assignRes.success) {
                                        setStatus('created');
                                        setShowSuccessModal(true);
                                    } else {
                                        setStatus('invalid');
                                    }
                                })
                                .catch(() => {
                                    setStatus('invalid');
                                });
                        } catch {
                            setStatus('invalid');
                        }
                        break;
                    case 'status':
                        setStatus(classification.status);
                        setPaymentLink(classification.paymentLink);
                        break;
                    case 'not_subscribed':
                        setStatus('none');
                        break;
                    default:
                        // A technical failure (e.g. the server's live Razorpay lookup timing
                        // out or erroring) is not proof the user has no subscription. Surface
                        // it as a retryable error instead of the 'none' status, which
                        // immediately redirects to /subscribe - see
                        // classifySubscriptionCheckResult() for why this distinction matters.
                        setError('Error fetching subscription status.');
                }
                setLoading(false);
            })
            .catch(() => {
                setError('Error fetching subscription status.');
                setLoading(false);
            });
    }, [navigate, refreshCount, isLoggedIn]);

    useEffect(() => {
        switch (status) {
            case 'active':
                setStatusMessage('Your subscription is active.');
                break;
            case 'created':
            case 'authenticated':
                setStatusMessage('Your subscription is being created. You will receive an email once it is active. Please do not make another payment.');
                break;
            case 'none':
                setStatusMessage('You do not have an active subscription.');
                break;
            case 'cancelled':
                setStatusMessage('Your subscription has been cancelled.');
                break;
            case 'expired':
                setStatusMessage('Your subscription has expired.');
                break;
            case 'halted':
                setStatusMessage('Your subscription is halted due to payment issues.');
                break;
            case 'unverified':
                setStatusMessage('Your subscription is unverified.');
                break;
            case 'invalid':
                setStatusMessage('Your subscription key is invalid.');
                break;
            default:
                setStatusMessage(`Your subscription status is ${status}.`);
        }
    }, [status]);

    useEffect(() => {
        if (!loading && status === 'active') {
            sessionStorage.setItem('isSubscribed', 'true');
            // Back to where the dashboard was headed when it sent us here, if it was
            // headed somewhere (EL-49) - see postCheckDestination.
            navigate(postCheckDestination(location.state), { replace: true });
        } else if (!loading &&
            (
                status === 'none'
                || status === 'halted'
                // || status === 'completed'
                // || status === 'cancelled'
                // || status === 'expired'
            )) {
            navigate('/subscribe');
        }
    }, [loading, status, navigate]);

    return (
        <>
            <Header type="small" showAccountDropdown={false} />
            <div className="min-h-[calc(100vh-220px)] flex flex-col items-center justify-center bg-background-dark font-sans">
                {loading && !error && (
                    <>
                        <Loading className="text-accent-main h-6 w-6" />
                        <div className="text-accent-main font-semibold mb-2">Checking subscription status...</div>
                    </>
                )}
                {!loading && error && (
                    <>
                        <AlertTriangle className="w-8 h-8 mb-2 text-red-500" />
                        <div className="text-red-700 font-semibold mb-2">{error}</div>
                    </>
                )}
                {!loading && !error && (
                    <>
                        {(status === 'cancelled' || status === 'expired') ? (
                            <>
                                <XCircle className="w-8 h-8 mb-2 text-red-500" />
                                <div className="text-red-700 font-semibold mb-2 text-center">{statusMessage} Please subscribe again to access your locker.</div>
                            </>
                        ) : (
                            <>
                                <AlertTriangle className="w-8 h-8 mb-2 text-yellow-500" />
                                <div className="text-yellow-700 font-semibold mb-2 ">{statusMessage}</div>
                                <div className="text-yellow-700 font-semibold mb-2 text-center">Please contact support for more information.</div>
                            </>
                        )}
                    </>
                )}
                <div className="mt-4 flex flex-row items-center gap-2">
                    {!loading && !error && (
                        <Button onClick={() => navigate('/subscribe')}>Go to Subscription</Button>
                    )}
                    {!loading && !error && paymentLink && (status === 'created' || status === 'unverified') && (
                        <Button onClick={() => window.open(paymentLink, '_blank')}>Check status</Button>
                    )}
                    <Button onClick={() => setRefreshCount(c => c + 1)}>Refresh</Button>
                </div>
                <span className="mt-6 text-sm text-gray-500">If you have already subscribed and are being redirected here, please wait a few moments and click "Refresh".</span>
                <span className="mt-2 text-xs text-gray-500">If the issue persists, contact Support.</span>
            </div>
            <ConfirmModal
                open={showSuccessModal}
                title="Subscription Successful!"
                message="Your subscription has been activated successfully. Please log in again to continue."
                confirmText="Okay"
                onConfirm={async () => {
                    await logout();
                    sessionStorage.clear();
                    navigate('/');
                }}
            />
        </>
    );
}

